import { css } from '@emotion/react';
import { FC, memo } from 'react';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { colors, spacing, fontStyles } from '../../../styles';
import SectionHeading from '../SectionHeading';
import { getSymbolKeyEvents, PortfolioKeyEvent } from '../../../apis/backend';
import SkeletonBox from '../../SkeletonBox';
import Button from '../../Button';
import * as stores from '../../../stores';
import EventCard from './EventCard';

// Constants
const MAX_VISIBLE_SYMBOLS = 3;

interface Props {
  symbol: string;
}

const KeyEvents: FC<Props> = memo(({ symbol }) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const purchaserInfo = stores.Purchase.useState((s) => s.purchaserInfo);
  const isSubscribed = purchaserInfo?.entitlements?.active[stores.entitlementIdentifier]?.isActive;

  const {
    data: keyEventsData,
    isLoading,
    error,
  } = useQuery(['symbolKeyEvents', symbol, i18n.language], () => getSymbolKeyEvents(symbol, 3), {
    enabled: !!symbol,
  });

  const handleEventClick = (event: PortfolioKeyEvent) => {
    if (event.route && event.route !== '') {
      history.push(event.route);
    }
  };

  const handleEventKeyDown = (event: React.KeyboardEvent, keyEvent: PortfolioKeyEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleEventClick(keyEvent);
    }
  };

  const handleSeeAllEventsClick = () => {
    if (isSubscribed) {
      history.push(`/symbol/${symbol}/key-events`);
    } else {
      stores.showPaymentModalByPreference('key_events_unlock_all');
    }
  };

  const renderErrorState = () => (
    <div
      css={css`
        text-align: center;
        padding: ${spacing.regular};
        color: ${colors.berry};
        ${fontStyles.dog};
      `}
    >
      {t('symbol.keyEventsError')}
    </div>
  );

  const renderLoadingState = () => (
    <div
      css={css`
        margin-top: ${spacing.deci};
      `}
    >
      <SkeletonBox width="100%" />
      <SkeletonBox width="100%" />
      <SkeletonBox width="100%" />
    </div>
  );

  // Don't render anything if there are no events and not loading
  if (!isLoading && !error && (!keyEventsData || keyEventsData.events.length === 0)) {
    return null;
  }

  return (
    <div
      css={css`
        margin-top: ${spacing.deka};
        margin-bottom: ${spacing.deka};
      `}
    >
      <SectionHeading text={t('symbol.keyEvents')} />
      {error ? (
        renderErrorState()
      ) : isLoading ? (
        renderLoadingState()
      ) : keyEventsData && keyEventsData.events.length > 0 ? (
        <div
          css={css`
            margin-top: ${spacing.deci};
          `}
        >
          {keyEventsData.events.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              onEventClick={handleEventClick}
              onEventKeyDown={handleEventKeyDown}
              maxVisibleSymbols={MAX_VISIBLE_SYMBOLS}
            />
          ))}
          <Button
            variant="secondary"
            block
            css={css`
              margin-top: ${spacing.deci};
            `}
            onClick={handleSeeAllEventsClick}
          >
            {isSubscribed ? t('symbol.seeAllEvents') : t('symbol.unlockAllEvents')}
          </Button>
        </div>
      ) : null}
    </div>
  );
});

KeyEvents.displayName = 'KeyEvents';

export default KeyEvents;
